import { useQuery, gql } from "@apollo/client"
import { offsetLimitPagination } from "@apollo/client/utilities"

const LOCATION_DAY_OPERATING_HOURS_FIELDS = gql`
  fragment LOCATION_DAY_OPERATING_HOURS_FIELDS on LocationDayOperatingHours {
    closed
    openAt {
      hour
      minute
    }
    closeAt {
      hour
      minute
    }
  }
`

const LOCATION_FIELDS = gql`
  fragment LocationFields on Location {
    id
    name
    timeZone
    region {
      id
      name
    }
    attributes {
      id
      integration
      key
      value
    }
    inactive
    unbilled
    logo {
      id
      key
    }
    subLocations {
      id
      name
    }
    address {
      latitude
      longitude
      fullAddress
      name
      line1
      line2
      city
      postcode
      country
    }
    operatingHours {
      monday {
        ...LOCATION_DAY_OPERATING_HOURS_FIELDS
      }
      tuesday {
        ...LOCATION_DAY_OPERATING_HOURS_FIELDS
      }
      wednesday {
        ...LOCATION_DAY_OPERATING_HOURS_FIELDS
      }
      thursday {
        ...LOCATION_DAY_OPERATING_HOURS_FIELDS
      }
      friday {
        ...LOCATION_DAY_OPERATING_HOURS_FIELDS
      }
      saturday {
        ...LOCATION_DAY_OPERATING_HOURS_FIELDS
      }
      sunday {
        ...LOCATION_DAY_OPERATING_HOURS_FIELDS
      }
    }
    manager {
      id
      firstName
      lastName
      fullName
      email
    }
    phoneNumber
    website
    uploads {
      id
      key
      fileName
      fileGroup
      fileSize
    }
  }
  ${LOCATION_DAY_OPERATING_HOURS_FIELDS}
`

const LOCATIONS_QUERY = gql`
  query Locations {
    locations {
      ...LocationFields
    }
  }
  ${LOCATION_FIELDS}
`

const useQueryLocations = (options) => useQuery(LOCATIONS_QUERY, options)

const LOCATIONS_V2_QUERY = gql`
  query Locations($filter: LocationsFilterInput, $limit: Int, $offset: Int) {
    locationsV2 {
      list(filter: $filter, limit: $limit, offset: $offset) {
        ...LocationFields
      }
      count(filter: $filter)
    }
  }
  ${LOCATION_FIELDS}
`

const useQueryLocationsV2 = (options = {}) => {
  const { variables, ...apolloOptions } = options
  return useQuery(LOCATIONS_V2_QUERY, {
    variables,
    ...apolloOptions
  })
}

const LOCATIONS_V2_COUNT_QUERY = gql`
  query LocationsCount($filter: LocationsFilterInput) {
    locationsV2 {
      count(filter: $filter)
    }
  }
`

const useQueryLocationsV2Count = (options = {}) => {
  const { variables, ...apolloOptions } = options
  return useQuery(LOCATIONS_V2_COUNT_QUERY, {
    variables,
    ...apolloOptions
  })
}

const locationsTypePolicies = {
  scoped: {
    LocationsQuery: {
      fields: {
        list: offsetLimitPagination(["filter"]),
        count: {
          keyArgs: ["filter"]
        }
      }
    }
  }
}

export {
  useQueryLocations,
  useQueryLocationsV2,
  useQueryLocationsV2Count,
  LOCATION_FIELDS,
  LOCATION_DAY_OPERATING_HOURS_FIELDS,
  LOCATIONS_QUERY,
  locationsTypePolicies
}
